import React, { useEffect, useRef, useState } from "react";
import { createRoot } from 'react-dom/client'
import { Viewer } from "../viewer/viewer";
import { createLogger } from "../viewer/logger";
import { ComponentRef } from "./componentRef";
import { ControllablePromise } from "../utils/promise";
import { ClientState as ClientState } from "../viewer/socketClient";

/**
 * Creates a UI container along with a VIM.Viewer and its associated React component.
 * @param container An optional container object. If none is provided, a container will be created.
 * @returns An object containing the resulting container, reactRoot, and viewer.
 */
export function createComponent (
  parent?: HTMLElement
) {
  const promise = new ControllablePromise<ComponentRef>()
  const reactRoot = createRoot(parent ?? document.createElement('div'))
  reactRoot.render(
    <UltraComponent onReady={(ref) => promise.resolve(ref)}></UltraComponent>
  )
  return promise.promise
}

export function UltraComponent(props : { onReady: (ref: ComponentRef) => void}){
  const div = useRef<HTMLDivElement>(null)
  
  const autoScroll = useRef(true)
  const [expandLogs, setExpandLogs] = useState(false)
  const [logContent, setLogContent] = useState('')
  const [useLogs, setUseLogs] = useState(false)

  const [msg, setMsg] = useState<string | undefined>(undefined)
  const text = useRef<HTMLTextAreaElement>(null)
  const unsubscribe = useRef<(() => void) | undefined>()
  const timeout = useRef<ReturnType<typeof setTimeout>>()

  // Runs every update, scroll to bottom if autoscroll is enabled
  useEffect(() => {
    if(autoScroll.current){
      text.current?.scroll(0, 100000)
    }
  })

  // Clear timeout after 5 seconds after a message is displayed
  useEffect(() => {
    clearTimeout(timeout.current)
    if(msg !== undefined){
      timeout.current = setTimeout(() => {
        setMsg(undefined)
      }, 5000)
    }
  },[msg])

  // Setup the viewer and logger
  useEffect(() => {
    const logger = createLogger((str) => setLogContent(c => c + '\n' + str))
    const viewer = Viewer.createWithCanvas(div.current!, logger)

    unsubscribe.current = viewer.onStateChanged.subscribe(() =>{
      clearTimeout(timeout.current)
      setMsg(connectionMessage(viewer.state, viewer.serverUrl ?? ''))
    })

    const log = {
      get enable() {
        return useLogs
      },
      set enable(use: boolean) {
        setUseLogs(use)
      }
    }
    props.onReady(new ComponentRef(viewer, setMsg, log))
  }, [])

  const msgbox = () => {
    const shadow = '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';
    return (
      <div className="msgbox" style={{position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: shadow, border: '1px solid black', borderRadius: '8px', backgroundColor: 'white', opacity: '80%', top: '40px', left: '30%', right: '30%', height: '60px', padding: '10px', overflow: 'hidden', whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
         <span style={{ fontSize: 'clamp(8px, 2vw, 16px)' }}>{msg}</span>
      </div>
    );
  };
  

  const logs = () => {
    return <div className="logs" style={{transition:'height 0.5s ease', position: 'absolute', right:'0px', bottom:'0px', width:'300px', height: (expandLogs ? '80%': '0%')}} ref={div}>
      <button onClick={() => setExpandLogs(!expandLogs)} style={{position:'absolute', right:'0px', top:'-20px', width:'40px', height:'20px', opacity:'100%' }}>logs</button>
      <textarea ref={text} value={logContent} readOnly={true} style={{ position:'absolute', borderWidth:(expandLogs ? '1px': '0px'), opacity:'80%', inset:'0px', padding:'0px', resize:'none'}}></textarea>
    </div>
  }

  return (
    <div style={{position:'relative', minHeight:'0px', height:'100%', width:'100%', textAlign:'center'}} ref={div}>
      {msg ? msgbox() : null}
      {useLogs ? logs() : null}
    </div>
  );
}

function connectionMessage(state: ClientState, url: string | undefined){
  switch(state.status){
    case 'connecting':
      return `Connecting to ${url}`
    case 'connected':
      return `Connected to ${url}`
    case 'disconnected':
      return `Cannot connect to ${url ?? "Missing URL"}. Please ensure the VIM Ultra process is running and that it can be reached by the local machine.`
  } 

}
